import { useEffect } from 'react';

import { useMatches, useParams } from 'react-router-dom';

import {
  useOptionalCurrentTenantID,
  useFindConnector,
  useFindConnectorFeature,
  useFindProvider,
} from '@/hooks/scoped';
import type { UUID } from '@/utils/uuid';

const APP_NAME = 'Hydden';

type RouteHandle = {
  breadcrumb?: string;
};

function formatTabLabel(tabId: string): string {
  return tabId.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Sets `document.title` based on the current route context.
 *
 * For resource-detail pages (connectors, providers) the title uses the
 * resource's display name instead of the generic route breadcrumb.
 * Tabbed connector pages append the tab label (e.g. "My Connector Logs").
 *
 * Pattern: `Page · Suffix` or `Page · Hydden` or just `Hydden`.
 */
export function useDocumentTitle(suffix?: string) {
  const matches = useMatches();
  const params = useParams() as Record<string, string | undefined>;

  const tenantId = useOptionalCurrentTenantID();
  const { data: connector } = useFindConnector(
    tenantId,
    params.connectorId as UUID | undefined
  );
  const { data: connectorFeature } = useFindConnectorFeature(
    tenantId,
    params.connectorId as UUID | undefined,
    params.featureId as UUID | undefined
  );
  const { data: provider } = useFindProvider(
    tenantId,
    params.providerId as UUID | undefined
  );

  useEffect(() => {
    const breadcrumb = [...matches]
      .reverse()
      .map((m) => (m.handle as RouteHandle | undefined)?.breadcrumb)
      .find((label) => typeof label === 'string' && label.length > 0);

    const resourceName =
      connectorFeature?.name ?? connector?.name ?? provider?.name;
    const tabLabel = params.tabId ? formatTabLabel(params.tabId) : '';

    let pageTitle: string | undefined;

    if (resourceName) {
      pageTitle = tabLabel ? `${resourceName} ${tabLabel}` : resourceName;
    } else {
      pageTitle = breadcrumb;
    }

    if (pageTitle && suffix) {
      document.title = `${pageTitle} · ${suffix}`;
    } else if (pageTitle) {
      document.title = `${pageTitle} · ${APP_NAME}`;
    } else if (suffix) {
      document.title = suffix;
    } else {
      document.title = APP_NAME;
    }
  }, [matches, suffix, connector, connectorFeature, provider, params.tabId]);
}
